import { SelectItem } from '@mantine/core/lib/Select/types';
import { CopyTranslations, FinancialTerms } from 'localization/localization-types';

const UK_FINANCIAL_TERMS_COPY: FinancialTerms = {
  preTaxPrice: 'Total Ex VAT',
  pretax: 'Subtotal',
  subtotal: 'Total Ex VAT',
  tax: 'VAT',
  taxRate: 'VAT%',
};

export const UK_COPY: CopyTranslations = {
  financial: UK_FINANCIAL_TERMS_COPY,
  address: {
    zipLabel: 'Postcode',
    zipPlaceholder: 'Enter Postcode',
    zipInputMask: [
      // London areas
      '^(E|EC|N|NW|SE|SW|W|WC)[0-9]{1,2}\\s?[0-9][A-Z]{2}$',

      // Major cities
      '^(B|BA|BB|BD|BH|BL|BN|BR|BS|BT)[0-9]{1,2}\\s?[0-9][A-Z]{2}$', // Birmingham, Bath, etc.
      '^(M|OL|SK|WA|WN)[0-9]{1,2}\\s?[0-9][A-Z]{2}$', // Manchester area
      '^(L|CH|PR|FY)[0-9]{1,2}\\s?[0-9][A-Z]{2}$', // Liverpool area
      '^(LS|BD|HG|YO|DN|S|HD)[0-9]{1,2}\\s?[0-9][A-Z]{2}$', // Yorkshire

      // Scotland
      '^(AB|DD|EH|FK|G|IV|KA|KY|ML|PA|PH|TD)[0-9]{1,2}\\s?[0-9][A-Z]{2}$',

      // Wales
      '^(CF|LD|LL|NP|SA|SY)[0-9]{1,2}\\s?[0-9][A-Z]{2}$',

      // Northern Ireland
      '^(BT)[0-9]{1,2}\\s?[0-9][A-Z]{2}$',
    ],
    regionLabel: 'County',
    regionPlaceholder: 'Select region',
  },
  components: {
    MoneyField: {
      priceInputLabel: 'Total',
      priceInputPlaceholder: 'Set total price',
    },
    AthletePackageDetail: {
      InvoicesTab: {
        packageBillingPlanInstallmentsThirdColumn: 'Installments (Total)',
        packageBillingPlanTotalPricePreTaxFirstColumn: 'Total Price',
      },
    },
    AthletePackageCreateForm: {
      invoicesSummaryTitle: 'Invoices (Total)',
    },
  },
};

// https://www.genuki.org.uk/big/Regions/Codes
export const UK_COUNTIES: SelectItem[] = [
  // England
  { label: 'Bedfordshire', value: 'BDF', group: 'England' },
  { label: 'Berkshire', value: 'BRK', group: 'England' },
  { label: 'Buckinghamshire', value: 'BKM', group: 'England' },
  { label: 'Cambridgeshire', value: 'CAM', group: 'England' },
  { label: 'Cheshire', value: 'CHS', group: 'England' },
  { label: 'Cornwall', value: 'CON', group: 'England' },
  { label: 'Cumberland', value: 'CUL', group: 'England' },
  { label: 'Derbyshire', value: 'DBY', group: 'England' },
  { label: 'Devon', value: 'DEV', group: 'England' },
  { label: 'Dorset', value: 'DOR', group: 'England' },
  { label: 'Durham', value: 'DUR', group: 'England' },
  { label: 'Essex', value: 'ESS', group: 'England' },
  { label: 'Gloucestershire', value: 'GLS', group: 'England' },
  { label: 'Hampshire', value: 'HAM', group: 'England' },
  { label: 'Herefordshire', value: 'HEF', group: 'England' },
  { label: 'Hertfordshire', value: 'HRT', group: 'England' },
  { label: 'Huntingdonshire', value: 'HUN', group: 'England' },
  { label: 'Kent', value: 'KEN', group: 'England' },
  { label: 'Lancashire', value: 'LAN', group: 'England' },
  { label: 'Leicestershire', value: 'LEI', group: 'England' },
  { label: 'Lincolnshire', value: 'LIN', group: 'England' },
  { label: 'London (City only)', value: 'LND', group: 'England' },
  { label: 'Middlesex', value: 'MDX', group: 'England' },
  { label: 'Norfolk', value: 'NFK', group: 'England' },
  { label: 'Northamptonshire', value: 'NTH', group: 'England' },
  { label: 'Northumberland', value: 'NBL', group: 'England' },
  { label: 'Nottinghamshire', value: 'NTT', group: 'England' },
  { label: 'Oxfordshire', value: 'OXF', group: 'England' },
  { label: 'Rutland', value: 'RUT', group: 'England' },
  { label: 'Shropshire', value: 'SAL', group: 'England' },
  { label: 'Somerset', value: 'SOM', group: 'England' },
  { label: 'Staffordshire', value: 'STS', group: 'England' },
  { label: 'Suffolk', value: 'SFK', group: 'England' },
  { label: 'Surrey', value: 'SRY', group: 'England' },
  { label: 'Sussex', value: 'SSX', group: 'England' },
  { label: 'Warwickshire', value: 'WAR', group: 'England' },
  { label: 'West Midlands', value: 'WMD', group: 'England' },
  { label: 'Westmorland', value: 'WES', group: 'England' },
  { label: 'Wiltshire', value: 'WIL', group: 'England' },
  { label: 'Worcestershire', value: 'WOR', group: 'England' },
  { label: 'Yorkshire', value: 'YKS', group: 'England' },
  { label: 'Yorkshire East Riding', value: 'ERY', group: 'England' },
  { label: 'Yorkshire North Riding', value: 'NRY', group: 'England' },
  { label: 'Yorkshire West Riding', value: 'WRY', group: 'England' },

  // Scotland
  { label: 'Aberdeenshire', value: 'ABD' },
  { label: 'Angus', value: 'ANS', group: 'Scotland' },
  { label: 'Argyllshire', value: 'ARL', group: 'Scotland' },
  { label: 'Ayrshire', value: 'AYR', group: 'Scotland' },
  { label: 'Banffshire', value: 'BAN', group: 'Scotland' },
  { label: 'Berwickshire', value: 'BEW', group: 'Scotland' },
  { label: 'Buteshire', value: 'BUT', group: 'Scotland' },
  { label: 'Caithness', value: 'CAI', group: 'Scotland' },
  { label: 'Clackmannanshire', value: 'CLK', group: 'Scotland' },
  { label: 'Dumfriesshire', value: 'DFS', group: 'Scotland' },
  { label: 'Dunbartonshire', value: 'DNB', group: 'Scotland' },
  { label: 'East Lothian', value: 'ELN', group: 'Scotland' },
  { label: 'Fife', value: 'FIF', group: 'Scotland' },
  { label: 'Inverness-shire', value: 'INV', group: 'Scotland' },
  { label: 'Kincardineshire', value: 'KCD', group: 'Scotland' },
  { label: 'Kinross-shire', value: 'KRS', group: 'Scotland' },
  { label: 'Kirkcudbrightshire', value: 'KKD', group: 'Scotland' },
  { label: 'Lanarkshire', value: 'LKS', group: 'Scotland' },
  { label: 'Midlothian', value: 'MLN', group: 'Scotland' },
  { label: 'Morayshire', value: 'MOR', group: 'Scotland' },
  { label: 'Nairnshire', value: 'NAI', group: 'Scotland' },
  { label: 'Orkney Isles', value: 'OKI', group: 'Scotland' },
  { label: 'Peeblesshire', value: 'PEE', group: 'Scotland' },
  { label: 'Perthshire', value: 'PER', group: 'Scotland' },
  { label: 'Renfrewshire', value: 'RFW', group: 'Scotland' },
  { label: 'Ross & Cromarty', value: 'ROC', group: 'Scotland' },
  { label: 'Roxburghshire', value: 'ROX', group: 'Scotland' },
  { label: 'Selkirkshire', value: 'SEL', group: 'Scotland' },
  { label: 'Shetland Isles', value: 'SHI', group: 'Scotland' },
  { label: 'Stirlingshire', value: 'STI', group: 'Scotland' },
  { label: 'Sutherland', value: 'SUT', group: 'Scotland' },
  { label: 'West Lothian', value: 'WLN', group: 'Scotland' },
  { label: 'Wigtownshire', value: 'WIG', group: 'Scotland' },

  // Wales
  { label: 'Anglesey', value: 'AGY', group: 'Wales' },
  { label: 'Brecknockshire', value: 'BRE', group: 'Wales' },
  { label: 'Caernarvonshire', value: 'CAE', group: 'Wales' },
  { label: 'Cardiganshire', value: 'CGN', group: ' Wales' },
  { label: 'Carmarthenshire', value: 'CMN', group: 'Wales' },
  { label: 'Denbighshire', value: 'DEN', group: 'Wales' },
  { label: 'Flintshire', value: 'FLN', group: 'Wales' },
  { label: 'Glamorgan', value: 'GLA', group: 'Wales' },
  { label: 'Merionethshire', value: 'MER', group: 'Wales' },
  { label: 'Monmouthshire', value: 'MON', group: 'Wales' },
  { label: 'Montgomeryshire', value: 'MGY', group: 'Wales' },
  { label: 'Pembrokeshire', value: 'PEM', group: ' Wales' },
  { label: 'Radnorshire', value: 'RAD', group: ' Wales' },

  // Northern Ireland
  { label: 'Antrim', value: 'ANT', group: 'Northern Ireland' },
  { label: 'Armagh', value: 'ARM', group: 'Northern Ireland' },
  { label: 'Down', value: 'DOW', group: 'Northern Ireland' },
  { label: 'Fermanagh', value: 'FER', group: 'Northern Ireland' },
  { label: 'Londonderry', value: 'LDY', group: 'Northern Ireland' },
  { label: 'Tyrone', value: 'TYR', group: 'Northern Ireland' },

  // Islands
  { label: 'Channel Islands', value: 'CHI', group: 'Islands' },
  { label: 'Alderney', value: 'ALD', group: 'Islands' },
  { label: 'Guernsey', value: 'GSY', group: 'Islands' },
  { label: 'Jersey', value: 'JSY', group: 'Islands' },
  { label: 'Sark', value: 'SRK', group: 'Islands' },
  { label: 'Isle of Man', value: 'IOM', group: 'Islands' },
  { label: 'Isle of Wight', value: 'IOW', group: 'Islands' },
];
